import { useState, useEffect } from 'react'
import { useLink } from '../../contexts/LinkContext'
import { useCampaign } from '../../contexts/CampaignContext'
import { useAuth } from '../../contexts/AuthContext'
import MarkdownText from '../MarkdownText'

function OverviewTab() {
  const [loading, setLoading] = useState(true)
  const [sessions, setSessions] = useState([])
  const [quests, setQuests] = useState([])
  const [roster, setRoster] = useState([])
  const { openEntity } = useLink()
  const { campaign, isModuleEnabled } = useCampaign()
  const { authEpoch } = useAuth()

  const sessionsEnabled = isModuleEnabled('sessions')
  const questsEnabled = isModuleEnabled('quests')
  const rosterEnabled = isModuleEnabled('character')

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      setLoading(true)
      try {
        const [sessionsData, questsData, rosterData] = await Promise.all([
          sessionsEnabled ? fetch('/api/sessions').then(res => res.json()) : Promise.resolve([]),
          questsEnabled ? fetch('/api/quests').then(res => res.json()) : Promise.resolve([]),
          rosterEnabled ? fetch('/api/roster').then(res => res.json()) : Promise.resolve([])
        ])

        if (cancelled) return
        setSessions(Array.isArray(sessionsData) ? sessionsData : [])
        setQuests(Array.isArray(questsData) ? questsData : [])
        setRoster(Array.isArray(rosterData) ? rosterData : [])
      } catch (err) {
        console.error('Error fetching data:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => { cancelled = true }
    // authEpoch: see the identical comment in ItemsTab.jsx.
  }, [sessionsEnabled, questsEnabled, rosterEnabled, authEpoch])

  // Latest session for the recap (sessions come back ordered by date desc).
  const latestSession = sessionsEnabled ? (sessions.find(s => s.is_latest === 1) || sessions[0] || null) : null
  const recapSummary = latestSession?.summary || ''
  const recapPreview = recapSummary.length > 320 ? recapSummary.slice(0, 320).trimEnd() + '…' : recapSummary

  // Active quests = visible and not completed.
  const activeQuests = questsEnabled ? quests.filter(q => q.is_visible === 1 && q.completed !== 1) : []

  const showRecapCard = sessionsEnabled && !loading && !!latestSession
  const showActiveQuests = questsEnabled && !loading && activeQuests.length > 0
  const showRoster = rosterEnabled && !loading && roster.length > 0
  const showEmptyState = !loading && !showRecapCard && !showActiveQuests && !showRoster

  const cardStyle = {
    background: 'var(--card-bg)',
    padding: '1.5rem',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    position: 'relative'
  }

  const sectionLabel = {
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: 'var(--accent-cyan)',
    marginBottom: '0.75rem'
  }

  return (
    <div className="tab-pane">
      <div className="page-header">
        <h1>Campaign Overview</h1>
        <p className="page-subtitle">Welcome to {campaign?.name || 'your campaign'}</p>
      </div>

      {showEmptyState && (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
          Nothing to show here yet — check back once your campaign has some content.
        </div>
      )}

      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Main column: recap + active quests */}
        <div style={{ flex: '1 1 360px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {showRecapCard && (
            <div
              onClick={() => openEntity('session', latestSession.id)}
              style={{
                ...cardStyle,
                cursor: 'pointer',
                borderColor: 'var(--accent-teal)',
                background: 'linear-gradient(135deg, #0d1520 0%, #0a0f18 100%)'
              }}
            >
              <div style={sectionLabel}>📜 Last time on…</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.35rem' }}>{latestSession.title}</h2>
                {latestSession.session_date && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{latestSession.session_date}</span>
                )}
              </div>
              {recapPreview && (
                <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 0.5rem' }}>
                  <MarkdownText style={{ whiteSpace: 'pre-line' }}>{recapPreview}</MarkdownText>
                </div>
              )}
              <span style={{ color: 'var(--accent-cyan)', fontWeight: 600, fontSize: '0.9rem' }}>Read the full recap →</span>
            </div>
          )}

          {showActiveQuests && (
            <div style={cardStyle}>
              <div style={sectionLabel}>⚔️ Active Quests</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                {activeQuests.map(quest => (
                  <button
                    key={quest.id}
                    onClick={() => openEntity('quest', quest.id)}
                    style={{
                      background: 'var(--primary-blue)',
                      border: '1px solid var(--accent-teal)',
                      color: 'var(--text-primary)',
                      padding: '0.5rem 0.9rem',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    {quest.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column: the party, vertical */}
        {showRoster && (
          <div style={{ ...cardStyle, flex: '0 0 240px' }}>
            <div style={sectionLabel}>🎭 The Party</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              {roster.map(pc => (
                <button
                  key={pc.id}
                  onClick={() => openEntity('character', pc.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', textAlign: 'left', padding: 0 }}
                  title={pc.character_name}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: pc.appearance_image_url ? 'transparent' : 'var(--accent-teal)',
                      border: '2px solid var(--accent-teal)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      color: 'var(--text-primary)',
                      overflow: 'hidden'
                    }}
                  >
                    {pc.appearance_image_url
                      ? <img src={pc.appearance_image_url} alt={pc.character_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : '🎭'}
                  </div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{pc.character_name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default OverviewTab
